import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import constants from 'constants/constants';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { UseMutationAddWidgetLayoutToReport } from 'api/mutations/addWidgetLayoutToReport.mutation';
import { useMutationSetComponent } from 'api/mutations/setComponent.mutation';
import { useMutationSetComponentSpecificFields } from 'api/mutations/setComponentSpecificFields.mutation';

import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { UpdateCreateWidget } from 'components/Dashboards/Reporting/reporting.types';

export interface ReportUrlParams {
    reportId: string;
}

type AddNarrativeArgs = {
    widgetId: IComponentProps['widgetId'];
    shareTitle?: string | undefined;
    projectId?: IComponentProps['projectId'];
    contractId?: IComponentProps['contractId'];
};

export interface AutomatedNarrativeParams {
    parentWidgetId?: number;
    parentComponentKey?: string;
    parentTitle?: string;
}

interface useAddAutomatedNarrativeWidgetToReportParams {
    parentComponentKey: string;
}

export function useAddAutomatedNarrativeWidgetToReport({
    parentComponentKey,
}: useAddAutomatedNarrativeWidgetToReportParams) {
    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const queryClient = useQueryClient();
    const reportId = Number(useParams<ReportUrlParams>().reportId);

    const { mutateAsync: addWidgetToReport } = useMutationSetComponent();
    const { mutateAsync: setWidgetSpecificFields } = useMutationSetComponentSpecificFields();
    const { mutateAsync: addWidgetLayoutToReport } = UseMutationAddWidgetLayoutToReport();

    const getWidgetState = (widgetId: number, title: string | undefined, parentComponentKey: string) => {
        const state: AutomatedNarrativeParams = {
            parentWidgetId: widgetId,
            parentTitle: title ?? 'Widget',
            parentComponentKey,
        };

        return JSON.stringify(state);
    };

    const onAddAutomatedNarrative = async ({ widgetId, shareTitle, projectId, contractId }: AddNarrativeArgs) => {
        const automatedNarrativeTitle = shareTitle ? `${shareTitle} Narrative` : 'Automated Narrative';

        const payload: UpdateCreateWidget = {
            id: null,
            componentKey: widgetsConfigurations.automatedNarrative.componentKey,
            contractId: contractId ?? constants.NO_PROJECT_CONTRACT_ID,
            projectId: projectId ?? constants.NO_PROJECT_CONTRACT_ID,
            reportId: reportId,
            params: getWidgetState(Number(widgetId), shareTitle, parentComponentKey),
            description: '',
            showNarrative: false,
            name: automatedNarrativeTitle,
        };

        await addWidgetToReport(payload, {
            onSuccess: async (res) => {
                const componentName = widgetsConfigurations[parentComponentKey]?.title || 'Unknown';
                sendEventWithDimensions({
                    category: 'Reporting',
                    action: 'Narrative Added',
                    label: componentName,
                });

                const createdWidgetId = res.id?.toString();
                const reportId = res.reportId;

                await addWidgetLayoutToReport({
                    reportId: reportId,
                    widgetId: createdWidgetId,
                    sourceWidgetId: Number(widgetId),
                });

                await setWidgetSpecificFields({
                    componentId: widgetId,
                    fieldsToUpdate: { showNarrative: true },
                });

                await queryClient.invalidateQueries(['getReport']);
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
        });
    };

    return { onAddAutomatedNarrative };
}
